import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import style from "../CommonStyles/commonformstyles.module.scss";
import { FaCloudArrowUp } from "react-icons/fa6";
import useMultipleFileUploadAws from "../../../../hooks/AWSFileUpload/useMultipleFileUploadAws";
import LinearProgressBar from "../../../../components/ProgressBar/LinearProgressBar";
import uploadAnimation from "../../../../assets/gif/uploadAnimation.gif";
import { useUpdateCertificate } from "../../../../hooks/partnerHook/usePartnerCertificates";

function EditCertificate() {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.partner.user);
  const certificate = useSelector((state) =>
    state.partner.certificates.find(
      (cert) => cert.certificate_id.toString() === certificateId
    )
  );

  let partnerId = null;
  if (userData) {
    partnerId = userData.partner.id;
  }

  const [formData, setFormData] = useState({
    licenceNo: "",
    title: "",
    issueDate: "",
    trainingInstitute: "",
    authNo: "",
    specialisation: "",
    certificateFile: null,
    mediaType: null,
    certificateUrl: "",
    additionalDetails: "",
  });

  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(uploadAnimation);
  const [dragging, setDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { uploadFileToS3, cancelUpload, mediaUrls, uploadProgresses } =
    useMultipleFileUploadAws();

  const {
    mutate: updateCertificateFn,
    isSuccess: certificateUpdated,
    isError: certificateError,
  } = useUpdateCertificate(navigate);

  // Pre-populate form with existing certificate data
  useEffect(() => {
    if (certificate) {
      setFormData({
        licenceNo: certificate.certificate_number || "",
        title: certificate.title || "",
        issueDate: certificate.certificate_issue
          ? new Date(certificate.certificate_issue).toISOString().split("T")[0]
          : "",
        trainingInstitute: certificate.institute_name || "",
        authNo: certificate.authorisation_number || "",
        specialisation: certificate.specialisation_category || "",
        certificateFile: null,
        mediaType: certificate.media?.[0]?.media_type || "image",
        certificateUrl: certificate.media?.[0]?.media_url || "",
        additionalDetails: certificate.additional_details || "",
      });
      setUploadPreview(certificate.media?.[0]?.media_url || uploadAnimation);
      setUploadSuccess(!!certificate.media?.[0]?.media_url);
    }
  }, [certificate]);

  useEffect(() => {
    if (formData.certificateFile && mediaUrls[formData.certificateFile.name]) {
      setFormData((prevData) => ({
        ...prevData,
        certificateUrl: mediaUrls[formData.certificateFile.name],
      }));
      setUploading(false);
      setUploadSuccess(true);
      setUploadPreview(mediaUrls[formData.certificateFile.name]);
    }
  }, [mediaUrls, formData.certificateFile]);

  const handleInputChange = (event) => {
    const { name, value, files } = event.target;
    if (name === "certificateFile") {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        const isImage = file.type.startsWith("image/");

        reader.onloadend = () => {
          setFormData((prevData) => ({
            ...prevData,
            certificateFile: file,
            certificateUrl: reader.result,
            mediaType: isImage ? "image" : "pdf",
          }));
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleBrowseClick = () => {
    document.getElementById("file-input").click();
  };

  const handleFileUpload = (file) => {
    if (!file) return;

    const validFormats = ["image/png", "image/jpeg"];
    if (!validFormats.includes(file.type)) {
      setErrorMessage("Invalid file format. Only PNG & JPG are allowed.");
      return;
    }

    setErrorMessage("");
    setFormData((prevData) => ({
      ...prevData,
      certificateFile: file,
      mediaType: "image",
    }));

    const reader = new FileReader();
    reader.onload = () => {
      setUploadPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCancel = () => {
    setFormData((prevData) => ({
      ...prevData,
      certificateFile: null,
      certificateUrl: certificate.media?.[0]?.media_url || "",
    }));
    setUploadPreview(certificate.media?.[0]?.media_url || uploadAnimation);
    setUploadSuccess(!!certificate.media?.[0]?.media_url);
  };

  const handleUpload = async () => {
    if (!formData.certificateFile) {
      return;
    }

    setUploading(true);

    try {
      console.log("📤 Uploading File:", formData.certificateFile.name);
      await uploadFileToS3(formData.certificateFile);
    } catch (error) {
      setUploading(false);
      setErrorMessage("Failed to upload the file. Please try again.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.certificateUrl) {
      setErrorMessage("Please upload a certificate image before submitting.");
      return;
    }

    const todayDate = new Date();
    const issueDate = new Date(formData.issueDate);

    if (issueDate > todayDate) {
      alert("Issue date cannot be in the future");
      return;
    }

    console.log("📤 Submitting Form:", formData);

    console.log(certificateId);

    const { certificateFile, ...certificateData } = formData;
    console.log("certificated data");
    console.log(certificateData);
    updateCertificateFn(
      { partnerId, certificateId, certificate: certificateData },
      {
        onSuccess: () => {
          setFormData({
            licenceNo: "",
            title: "",
            issueDate: "",
            trainingInstitute: "",
            authNo: "",
            specialisation: "",
            certificateFile: null,
            mediaType: null,
            certificateUrl: "",
            additionalDetails: "",
          });
          setUploadSuccess(false);
          setUploading(false);
          setUploadPreview(uploadAnimation);
          setErrorMessage("");
          navigate("/dashboard/certificates");
        },
        onError: (error) => {
          console.error(
            "Submission failed:",
            error.response?.data || error.message
          );
          setErrorMessage(
            `Submission failed: ${
              error.response?.data?.message || "Unknown error"
            }`
          );
        },
      }
    );
  };

  if (!certificate) {
    return <h2>Error: Certificate not found!</h2>;
  }

  return (
    <div className={style.dashboardFormContainerWrapper}>
      <div className={style.dashboardFormContainer}>
        <form className={style.FormContainer} onSubmit={handleSubmit}>
          <h2 className={style.dashboardFormContinerHeading}>
            Edit Certificate
          </h2>
          <p className={style.noEquipmentDescription}>
            Update details about your advanced training and qualifications.
          </p>

          <div className={style.dashboardFormInputLabelField}>
            <label className={style.dashFormContainerLabel} htmlFor="title">
              Certificate Title
            </label>
            <input
              id="title"
              name="title"
              placeholder="Enter certificate title"
              type="text"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className={style.dashboardFormInputLabelField}>
            <label className={style.dashFormContainerLabel} htmlFor="licenceNo">
              Certificate Number
            </label>
            <input
              id="licenceNo"
              name="licenceNo"
              placeholder="Ex. PC1023****05ZR"
              type="text"
              value={formData.licenceNo}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className={style.dashboardFormInputLabelField}>
            <label className={style.dashFormContainerLabel} htmlFor="issueDate">
              Date of Initial Issue:
            </label>
            <input
              id="issueDate"
              name="issueDate"
              type="date"
              value={formData.issueDate}
              onChange={handleInputChange}
              className={style.formDate}
              required
            />
          </div>

          <div className={style.dashboardFormInputLabelField}>
            <label
              className={style.dashFormContainerLabel}
              htmlFor="trainingInstitute"
            >
              Training Institute Name:
            </label>
            <input
              id="trainingInstitute"
              name="trainingInstitute"
              placeholder="E.g: Drone Verse Private Limited"
              type="text"
              value={formData.trainingInstitute}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className={style.dashboardFormInputLabelField}>
            <label className={style.dashFormContainerLabel} htmlFor="authNo">
              Training Authorisation Number
            </label>
            <input
              id="authNo"
              name="authNo"
              placeholder="Enter Authorisation Number"
              type="text"
              value={formData.authNo}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className={style.dashboardFormInputLabelField}>
            <label
              className={style.dashFormContainerLabel}
              htmlFor="specialisation"
            >
              Category of Specialisation
            </label>
            <input
              id="specialisation"
              name="specialisation"
              placeholder="E.g: Rotorcraft"
              type="text"
              value={formData.specialisation}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className={style.dashboardFormInputLabelField}>
            <label
              className={style.dashFormContainerLabel}
              htmlFor="additionalDetails"
            >
              Additional Remark
            </label>
            <textarea
              id="additionalDetails"
              name="additionalDetails"
              placeholder="Extra Details (Optional)"
              value={formData.additionalDetails}
              onChange={handleInputChange}
            ></textarea>
          </div>

          <h3 className={style.labels}>Upload Image</h3>
          <div
            className={style.DragUploader}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div
              className={`${style.DragUploaderImageContainer} ${
                dragging ? style.dragActive : ""
              }`}
            >
              {!formData.certificateFile && !formData.certificateUrl ? (
                <>
                  <FaCloudArrowUp className={style.uploadIcon} />
                  <p className={style.uploadText}>Drop images here</p>
                  <p className={style.supportedFormats}>
                    Supported formats: PNG, JPG
                  </p>
                  <p className={style.orText}>OR</p>
                  <h2 className={style.browseText} onClick={handleBrowseClick}>
                    <strong>Browse Images</strong>
                  </h2>
                </>
              ) : (
                <img
                  src={uploadPreview}
                  alt="Preview"
                  className={style.imagePreview}
                />
              )}

              <input
                id="file-input"
                type="file"
                accept="image/png, image/jpeg"
                className={style.fileInput}
                onChange={(e) => handleFileUpload(e.target.files[0])}
                hidden
              />
            </div>

            <div className={style.buttonContainer}>
              <span className={style.cancelText} onClick={handleCancel}>
                Cancel
              </span>
              <button
                type="button"
                className={style.uploadButton}
                onClick={handleUpload}
                disabled={uploadSuccess}
              >
                {uploadSuccess ? "Uploaded" : "Upload"}
              </button>
            </div>

            {errorMessage && (
              <p className={style.errorMessage}>{errorMessage}</p>
            )}
          </div>

          <div className={style.btnContainer}>
            <button className={style.dashboardFormSaveButtonDown} type="submit">
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditCertificate;
